import React, { useState } from "react";
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import {
  Box,
  Button,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  Today as TodayIcon,
  PlaylistAdd as MultiAddIcon,
  Delete as DeleteIcon,
  CheckCircleOutline as ApproveIcon,
  HighlightOff as DisapproveIcon,
  Save as SaveIcon,
  Close as CancelIcon,
  BorderColor as MultiEditIcon,
  CalendarMonth as YearlyIcon,
  ViewModule as MonthlyIcon,
  FilterAlt as FilterIcon,
  FilterAltOff as FilterOffIcon,
  FileDownload as DownloadIcon,
  ManageAccounts as ManageDelegatesIcon,
  PersonPin as MyCalendarIcon,
} from "@mui/icons-material";
import { format, startOfMonth, endOfMonth } from "date-fns";
import * as FileSaver from "file-saver";
import XLSX from "sheetjs-style";

const CalendarToolbar = ({
    onExportScreenshot,
  users,
  selectedUserId,
  selectedUser,
  currentMonth,
  multiEditMode,
  multiEditSaving,
  dirtyCount,
  isOwnCalendar,
  isDelegateForSelectedUser,
  canEdit,
  isAdminOrManager,
  isManager,
  selectedEntryIds,
  viewMode,
  selectedYear,
  filterStartDate,
  filterEndDate,
  excelData,
  exportFileName,
  onFilterStartDateChange,
  onFilterEndDateChange,
  onUserChange,
  onPrevMonth,
  onNextMonth,
  onToday,
  onOpenMultiAdd,
  onEnterMultiEdit,
  onExitMultiEdit,
  onMultiEditSave,
  onBulkDelete,
  onBulkApprove,
  onOpenBulkDisapprove,
  onToggleViewMode,
  onPrevYear,
  onNextYear,
  onManageDelegates,
  onGoToMyCalendar,
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const hasActiveFilter = !!(filterStartDate || filterEndDate);

  const handleExport = () => {
    if (!excelData || excelData.length === 0) return;
    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([buf], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });
    FileSaver.saveAs(blob, (exportFileName || "TravelCalendar") + ".xlsx");
  };

  return (

    <Paper sx={{ mb: 2, p: { xs: 1, sm: 2 } }}>
      <Grid container spacing={2} alignItems="stretch">
        {/* User selector and chip */}
        <Grid item xs={12} sm={3} md={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>User</InputLabel>
              <Select value={selectedUserId} onChange={onUserChange} label="User">
                {users.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.name || u.username}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedUser && (
              <Chip
                label={selectedUser.name || selectedUser.username}
                color="primary"
                variant="outlined"
                sx={{ fontSize: { xs: '0.9rem', sm: '1rem' }, p: { xs: 0.5, sm: 1 } }}
              />
            )}
            {isDelegateForSelectedUser && (
              <Tooltip title="You have been granted edit access to this calendar" arrow>
                <Chip
                  label="Delegate"
                  size="small"
                  color="warning"
                  variant="filled"
                  sx={{ fontSize: '0.7rem', height: 22 }}
                />
              </Tooltip>
            )}
            {!isOwnCalendar && onGoToMyCalendar && (
              <Tooltip title="Go to my calendar" arrow>
                <IconButton size="small" color="primary" onClick={onGoToMyCalendar}>
                  <MyCalendarIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Grid>

        {/* Month/Year navigation */}
        <Grid item xs={12} sm={5} md={4}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: { xs: 'flex-start', sm: 'center' }, gap: 1, flexWrap: 'wrap' }}>
            {viewMode === "yearly" ? (
              <>
                <IconButton onClick={onPrevYear} size="small">
                  <ChevronLeft />
                </IconButton>
                <Typography variant="h6" sx={{ minWidth: 120, textAlign: "center", fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  {selectedYear}
                </Typography>
                <IconButton onClick={onNextYear} size="small">
                  <ChevronRight />
                </IconButton>
                <Tooltip title="Go to today">
                  <IconButton onClick={onToday} size="small" color="primary">
                    <TodayIcon />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <>
                <IconButton onClick={onPrevMonth} size="small">
                  <ChevronLeft />
                </IconButton>
                <Typography variant="h6" sx={{ minWidth: 120, textAlign: "center", fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  {format(currentMonth, "MMMM yyyy")}
                </Typography>
                <IconButton onClick={onNextMonth} size="small">
                  <ChevronRight />
                </IconButton>
                <Tooltip title="Go to today">
                  <IconButton onClick={onToday} size="small" color="primary">
                    <TodayIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}
            <Tooltip title={viewMode === "monthly" ? "Yearly View" : "Monthly View"}>
              <IconButton onClick={onToggleViewMode} size="small" color="secondary">
                {viewMode === "monthly" ? <YearlyIcon /> : <MonthlyIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        </Grid>

        {/* Action buttons, responsive wrap */}
        <Grid item xs={12} sm={4} md={6}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: { xs: 'flex-start', sm: 'flex-end' }, gap: 1 }}>
            {multiEditMode ? (
              <>
                <Button
                  variant="contained"
                  size="small"
                  color="success"
                  startIcon={<SaveIcon />}
                  onClick={onMultiEditSave}
                  disabled={multiEditSaving || dirtyCount === 0}
                  sx={{ minWidth: 90 }}
                >
                  {multiEditSaving ? "Saving..." : `Save All (${dirtyCount})`}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CancelIcon />}
                  onClick={onExitMultiEdit}
                  disabled={multiEditSaving}
                  sx={{ minWidth: 80 }}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <>
                {canEdit && (
                  <>
                                 {isOwnCalendar && (
                  <Tooltip title="Manage who can edit your calendar" arrow>
                    <IconButton size="small" color="primary" onClick={onManageDelegates}>
                      <ManageDelegatesIcon />
                    </IconButton>
                  </Tooltip>
                )}
                    <Button
                      variant="outlined"
                      startIcon={<MultiAddIcon />}
                      onClick={onOpenMultiAdd}
                      size="small"
                      sx={{ minWidth: 90 }}
                    >
                      Multi Add
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<MultiEditIcon />}
                      onClick={onEnterMultiEdit}
                      size="small"
                      sx={{ minWidth: 90 }}
                    >
                      Multi Edit
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={onBulkDelete}
                      disabled={selectedEntryIds.length === 0}
                      sx={{ minWidth: 90 }}
                    >
                      Delete ({selectedEntryIds.length})
                    </Button>
                  </>
                )}
                {isManager && (
                  <>
                    <Button
                      variant="contained"
                      size="small"
                      color="success"
                      startIcon={<ApproveIcon />}
                      onClick={onBulkApprove}
                      disabled={selectedEntryIds.length === 0}
                      sx={{ minWidth: 90 }}
                    >
                      Approve ({selectedEntryIds.length})
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      color="error"
                      startIcon={<DisapproveIcon />}
                      onClick={onOpenBulkDisapprove}
                      disabled={selectedEntryIds.length === 0}
                      sx={{ minWidth: 110 }}
                    >
                      Disapprove ({selectedEntryIds.length})
                    </Button>
                  </>
                )}
              </>
            )}
            {/* Filter & Export icons — monthly and yearly view */}
            {((viewMode === "monthly" && !multiEditMode) || viewMode === "yearly") && (
              <>
                {viewMode === "monthly" && (
                  <Tooltip title={showFilters ? "Hide Filters" : "Filter by Date"}>
                    <IconButton
                      size="small"
                      color={hasActiveFilter ? "warning" : "default"}
                      onClick={() => setShowFilters((v) => !v)}
                    >
                      {hasActiveFilter ? <FilterOffIcon /> : <FilterIcon />}
                    </IconButton>
                  </Tooltip>
                )}
                {viewMode === "monthly" && (
                  <Tooltip title="Export to Excel">
                    <IconButton size="small" color="primary" onClick={handleExport}>
                      <DownloadIcon />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Export Screenshot">
                  <IconButton size="small" color="secondary" onClick={onExportScreenshot}>
                    <PhotoCameraIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Collapsible date filter row, responsive */}
      {viewMode === "monthly" && showFilters && (
        <Grid container spacing={2} alignItems="center" sx={{ mt: 1 }}>
          <Grid item xs={12} sm={4} md={2}>
            <TextField
              label="From"
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={filterStartDate}
              onChange={(e) => onFilterStartDateChange(e.target.value)}
              inputProps={{
                min: format(startOfMonth(currentMonth), "yyyy-MM-dd"),
                max: format(endOfMonth(currentMonth), "yyyy-MM-dd"),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <TextField
              label="To"
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={filterEndDate}
              onChange={(e) => onFilterEndDateChange(e.target.value)}
              inputProps={{
                min: filterStartDate || format(startOfMonth(currentMonth), "yyyy-MM-dd"),
                max: format(endOfMonth(currentMonth), "yyyy-MM-dd"),
              }}
            />
          </Grid>
          {hasActiveFilter && (
            <Grid item xs={12} sm={4} md={2}>
              <Tooltip title="Clear Filters">
                <IconButton
                  size="small"
                  color="warning"
                  onClick={() => {
                    onFilterStartDateChange("");
                    onFilterEndDateChange("");
                  }}
                >
                  <FilterOffIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          )}
        </Grid>
      )}
    </Paper>
  );
};

export default React.memo(CalendarToolbar);
